// Helper utilitario para Importación y Exportación de CSV de Clientes y Leads
// Cumple con la especificación RFC-4180 y soporte de codificación UTF-8 con BOM para Excel

export const CSV_VALID_PHASES = [
  'Cliente potencial',
  'Propuesta en curso',
  'Cerrado',
  'Descartado'
];

export const CSV_VALID_CHANNELS = [
  'Directo',
  'Fiverr',
  'Web Equipo Alerce',
  'Google Maps',
  'Google Search',
  'LinkedIn',
  'Referido'
];

// Parser RFC-4180 robusto para cadenas CSV
export function parseCSV(text) {
  if (!text || typeof text !== 'string') return [];

  // Remover BOM si viene al inicio
  const cleanText = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;

  const rows = [];
  let currentRow = [];
  let currentToken = '';
  let inQuotes = false;

  for (let i = 0; i < cleanText.length; i++) {
    const char = cleanText[i];
    const nextChar = cleanText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentToken += '"';
        i++; // saltar comilla escapada
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      currentRow.push(currentToken.trim());
      currentToken = '';
    } else if ((char === '\r' || char === '\n') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
      currentRow.push(currentToken.trim());
      // Solo añadir si no es una fila totalmente vacía
      if (currentRow.some(cell => cell.length > 0)) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentToken = '';
    } else {
      currentToken += char;
    }
  }

  if (currentToken.length > 0 || currentRow.length > 0) {
    currentRow.push(currentToken.trim());
    if (currentRow.some(cell => cell.length > 0)) {
      rows.push(currentRow);
    }
  }

  if (rows.length === 0) return [];

  // Mapeo flexible de nombres de columnas a campos de cliente
  const rawHeaders = rows[0].map(h => h.trim().toLowerCase());
  const headerMap = {};

  rawHeaders.forEach((h, index) => {
    // Normalización de tildes
    const norm = h.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (norm === 'nombre' || norm === 'name' || norm === 'contacto' || norm === 'cliente') {
      headerMap['name'] = index;
    } else if (norm === 'empresa' || norm === 'company' || norm === 'marca' || norm === 'negocio') {
      headerMap['company'] = index;
    } else if (norm === 'mail' || norm === 'email' || norm === 'correo') {
      headerMap['email'] = index;
    } else if (norm === 'telefono' || norm === 'phone' || norm === 'whatsapp' || norm === 'celular') {
      headerMap['phone'] = index;
    } else if (norm === 'fase' || norm === 'phase' || norm === 'etapa' || norm === 'estado comercial') {
      headerMap['phase'] = index;
    } else if (norm === 'canal' || norm === 'channel' || norm === 'origen' || norm === 'fuente') {
      headerMap['channel'] = index;
    } else if (norm === 'pais' || norm === 'country' || norm === 'nacion') {
      headerMap['country'] = index;
    } else if (norm === 'grupos' || norm === 'groups' || norm === 'etiquetas' || norm === 'tags' || norm === 'categorias') {
      headerMap['groups'] = index;
    } else if (norm === 'redes' || norm === 'socials' || norm === 'web' || norm === 'website' || norm === 'link') {
      headerMap['socials'] = index;
    } else if (norm === 'observaciones' || norm === 'notas' || norm === 'notes' || norm === 'comentarios') {
      headerMap['notes'] = index;
    } else if (norm === 'no contactar' || norm === 'do_not_contact' || norm === 'bloqueado' || norm === 'dnc') {
      headerMap['do_not_contact'] = index;
    }
  });

  const records = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const record = {
      _rowNumber: r + 1,
      name: headerMap['name'] !== undefined ? (row[headerMap['name']] || '').trim() : '',
      company: headerMap['company'] !== undefined ? (row[headerMap['company']] || '').trim() : '',
      email: headerMap['email'] !== undefined ? (row[headerMap['email']] || '').trim() : '',
      phone: headerMap['phone'] !== undefined ? (row[headerMap['phone']] || '').trim() : '',
      phase: headerMap['phase'] !== undefined ? (row[headerMap['phase']] || '').trim() : '',
      channel: headerMap['channel'] !== undefined ? (row[headerMap['channel']] || '').trim() : '',
      country: headerMap['country'] !== undefined ? (row[headerMap['country']] || '').trim() : '',
      groups: headerMap['groups'] !== undefined ? (row[headerMap['groups']] || '').trim() : '',
      socials: headerMap['socials'] !== undefined ? (row[headerMap['socials']] || '').trim() : '',
      notes: headerMap['notes'] !== undefined ? (row[headerMap['notes']] || '').trim() : '',
      do_not_contact: headerMap['do_not_contact'] !== undefined ? (row[headerMap['do_not_contact']] || '').trim() : ''
    };
    records.push(record);
  }

  return records;
}

// Validación y preparación de filas de clientes detectando duplicados y errores
export function validateAndPrepareClientRows(records, existingClients = []) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const processedEmails = new Set();
  const processedPhones = new Set();
  const processedNames = new Set();

  // Indexar clientes existentes de la base de datos
  const existingEmails = new Set();
  const existingPhones = new Set();
  const existingNames = new Set();

  existingClients.forEach(c => {
    if (c.email) existingEmails.add(c.email.toLowerCase().trim());
    (c.contacts || []).forEach(cnt => {
      if (cnt.email) existingEmails.add(cnt.email.toLowerCase().trim());
    });

    const phoneClean = (c.phone || '').replace(/\D/g, '');
    if (phoneClean.length >= 6) {
      existingPhones.add(phoneClean.slice(-8));
    }
    if (c.name) {
      existingNames.add(c.name.toLowerCase().trim());
    }
  });

  return records.map(record => {
    const errors = [];
    let isDuplicate = false;
    let duplicateReason = '';

    // 1. Campo obligatorio: Nombre
    if (!record.name) {
      errors.push('El Nombre es obligatorio.');
    }

    // 2. Validación de Formato de Email
    let cleanEmail = (record.email || '').toLowerCase().trim();
    if (cleanEmail && !emailRegex.test(cleanEmail)) {
      errors.push(`Email no tiene un formato válido (${record.email}).`);
    }

    // 3. Normalización y validación de Fase comercial
    let cleanPhase = 'Cliente potencial';
    if (record.phase) {
      const matchPhase = CSV_VALID_PHASES.find(p => p.toLowerCase() === record.phase.toLowerCase().trim());
      if (matchPhase) {
        cleanPhase = matchPhase;
      } else {
        errors.push(`Fase no reconocida ("${record.phase}"). Opciones válidas: ${CSV_VALID_PHASES.join(', ')}.`);
      }
    }

    // 4. Canal de origen
    let cleanChannel = record.channel ? record.channel.trim() : 'Directo';
    const matchChannel = CSV_VALID_CHANNELS.find(ch => ch.toLowerCase() === cleanChannel.toLowerCase());
    if (matchChannel) {
      cleanChannel = matchChannel;
    }

    // 5. Normalización de País
    let cleanCountry = record.country ? record.country.trim() : 'Argentina';

    // 6. Normalización de Grupos (convertir a Array)
    let cleanGroups = [];
    if (record.groups) {
      cleanGroups = record.groups
        .split(/[,;]/)
        .map(g => g.trim())
        .filter(Boolean);
    }
    if (cleanGroups.length === 0) {
      cleanGroups = ['prospecto'];
    }

    // 7. Normalización de do_not_contact
    let cleanDnc = false;
    if (record.do_not_contact) {
      const dncStr = record.do_not_contact.toLowerCase().trim();
      if (['si', 'sí', 'true', '1', 'yes'].includes(dncStr)) {
        cleanDnc = true;
      }
    }

    // 8. Detección de Duplicados (contra DB y contra filas anteriores del mismo archivo)
    const cleanPhoneDigits = (record.phone || '').replace(/\D/g, '');
    const phoneFragment = cleanPhoneDigits.length >= 6 ? cleanPhoneDigits.slice(-8) : null;
    const cleanName = (record.name || '').toLowerCase().trim();

    // A. Por email
    if (cleanEmail) {
      if (existingEmails.has(cleanEmail)) {
        isDuplicate = true;
        duplicateReason = `Email ya registrado en el CRM (${cleanEmail})`;
      } else if (processedEmails.has(cleanEmail)) {
        isDuplicate = true;
        duplicateReason = `Email repetido en este mismo CSV (${cleanEmail})`;
      }
    }

    // B. Por teléfono
    if (!isDuplicate && phoneFragment) {
      if (existingPhones.has(phoneFragment)) {
        isDuplicate = true;
        duplicateReason = `Teléfono ya existente en el CRM (...${phoneFragment})`;
      } else if (processedPhones.has(phoneFragment)) {
        isDuplicate = true;
        duplicateReason = `Teléfono repetido en este mismo CSV (...${phoneFragment})`;
      }
    }

    // C. Por nombre idéntico si no tiene email ni teléfono
    if (!isDuplicate && !cleanEmail && !phoneFragment && cleanName) {
      if (existingNames.has(cleanName)) {
        isDuplicate = true;
        duplicateReason = `Nombre idéntico a cliente ya existente ("${record.name}")`;
      } else if (processedNames.has(cleanName)) {
        isDuplicate = true;
        duplicateReason = `Nombre repetido en este mismo CSV ("${record.name}")`;
      }
    }

    // Registrar para evitar duplicados en filas subsiguientes del archivo
    if (cleanEmail) processedEmails.add(cleanEmail);
    if (phoneFragment) processedPhones.add(phoneFragment);
    if (cleanName) processedNames.add(cleanName);

    const status = errors.length > 0 ? 'error' : (isDuplicate ? 'duplicate' : 'valid');

    return {
      _rowNumber: record._rowNumber,
      status,
      errors,
      duplicateReason,
      clientData: {
        name: record.name,
        company: record.company || record.name,
        email: cleanEmail || null,
        phone: record.phone ? record.phone.trim() : null,
        phase: cleanPhase,
        channel: cleanChannel,
        country: cleanCountry,
        groups: cleanGroups,
        socials: record.socials ? record.socials.trim() : null,
        notes: record.notes ? record.notes.trim() : null,
        do_not_contact: cleanDnc
      }
    };
  });
}

// Generación de la plantilla CSV de ejemplo para descargar
export function generateCSVTemplate() {
  const headers = [
    'Nombre',
    'Empresa',
    'Email',
    'Telefono',
    'Fase',
    'Canal',
    'Pais',
    'Grupos',
    'Redes',
    'Observaciones',
    'No Contactar'
  ];

  const sampleRows = [
    [
      'Agustín Méndez',
      'Distribuidora Del Sur',
      'agustin@distribuidoradelsur.com',
      '+54 9 11 4455-6677',
      'Cliente potencial',
      'Directo',
      'Argentina',
      'distribucion, b2b',
      'https://linkedin.com/in/agustinmendez',
      'Interesado en diagnóstico inicial de procesos y stock',
      'no'
    ],
    [
      'Valeria Rossi',
      'Estudio Contable Rossi & Asoc.',
      'valeria@estudiorossi.com.ar',
      '+54 9 341 556-7890',
      'Propuesta en curso',
      'Web Equipo Alerce',
      'Argentina',
      'contable, pyme',
      'https://estudiorossi.com.ar',
      'Cargó formulario web de diagnóstico de tareas',
      'no'
    ]
  ];

  const escapeCell = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const csvLines = [
    headers.map(escapeCell).join(','),
    ...sampleRows.map(row => row.map(escapeCell).join(','))
  ];

  // Prefijar con UTF-8 BOM (\uFEFF) para compatibilidad total con Excel
  return '\uFEFF' + csvLines.join('\r\n');
}

// Exportación de clientes filtrados a formato CSV
export function exportClientsToCSV(clients = []) {
  const headers = [
    'ID',
    'Nombre',
    'Empresa',
    'Fase Comercial',
    'Canal',
    'Pais',
    'Email Principal',
    'Telefono',
    'Grupos',
    'Redes / Web',
    'No Contactar',
    'Cantidad Contactos',
    'Observaciones',
    'Fecha Alta'
  ];

  const escapeCell = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = clients.map(c => {
    const contactsCount = (c.contacts || []).length;
    const groupsStr = Array.isArray(c.groups) ? c.groups.join(', ') : (c.groups || '');
    const dateStr = c.created_at ? new Date(c.created_at).toLocaleDateString('es-AR') : '';

    return [
      c.id,
      c.name || '',
      c.company || '',
      c.phase || '',
      c.channel || '',
      c.country || '',
      c.email || '',
      c.phone || '',
      groupsStr,
      c.socials || '',
      c.do_not_contact ? 'SI' : 'NO',
      contactsCount,
      c.notes || '',
      dateStr
    ].map(escapeCell).join(',');
  });

  return '\uFEFF' + [headers.map(escapeCell).join(','), ...rows].join('\r\n');
}
